import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow, parseISO } from "date-fns";
import { Bell, CheckCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useT } from "@/lib/translation";
import { useNotifications } from "../hooks/useNotifications";
import { notificationText } from "../utils/notificationText";
import { useLocale } from "@/hooks/useLocale";

export function NotificationBell() {
  const navigate = useNavigate();
  const t = useT();
  const { dateFnsLocale } = useLocale();
  const [open, setOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll } =
    useNotifications();

  const handleClick = (id: number, link?: string | null) => {
    markAsRead(id);
    setOpen(false);
    if (link) navigate(link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 relative"
          title={t("notifications:bell.title")}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="text-sm font-semibold">
            {t("notifications:bell.title")}
          </span>
          {notifications.length > 0 && (
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  title={t("notifications:bell.markAllRead")}
                  onClick={markAllAsRead}
                >
                  <CheckCheck className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                title={t("notifications:bell.clearAll")}
                onClick={clearAll}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {t("notifications:bell.empty")}
          </div>
        ) : (
          <ScrollArea className="h-[360px]">
            {notifications.map((n) => {
              const text = notificationText(t, n);
              return (
                <button
                  key={n.id}
                  onClick={() => handleClick(n.id, n.link)}
                  className={`w-full text-left px-4 py-3 border-b last:border-0 transition-colors hover:bg-muted/50 flex gap-3 items-start ${!n.is_read ? "bg-muted/20" : ""}`}
                >
                  {!n.is_read && (
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                  )}
                  <div className={`min-w-0 ${n.is_read ? "pl-5" : ""}`}>
                    <p className="text-sm font-medium leading-snug truncate">
                      {text.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {text.body}
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-1">
                      {formatDistanceToNow(parseISO(n.created_at), {
                        addSuffix: true,
                        locale: dateFnsLocale,
                      })}
                    </p>
                  </div>
                </button>
              );
            })}
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}
